// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useDebounce } from '@/hooks/useDebounce';
import { supabase } from '@/lib/supabase';
import { CheckCircle, AlertCircle, User } from 'lucide-react';

interface FoundAthlete {
  id: string;
  username: string;
  email: string;
  avatar_url?: string;
}

interface RequestAssignmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (athleteProfileId: string, message?: string) => Promise<void>;
}

const RequestAssignmentsModal: React.FC<RequestAssignmentsModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [athleteEmail, setAthleteEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [foundAthlete, setFoundAthlete] = useState<FoundAthlete | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const { toast } = useToast();

  // Debounce the email input to avoid excessive API calls
  const debouncedEmail = useDebounce(athleteEmail, 300);

  // Search for athlete when debounced email changes
  useEffect(() => {
    const searchAthlete = async () => {
      if (!debouncedEmail.trim()) {
        setFoundAthlete(null);
        setSearchError(null);
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(debouncedEmail)) {
        setFoundAthlete(null);
        setSearchError('Please enter a valid email address');
        return;
      }

      setIsSearching(true);
      setSearchError(null);

      try {
        console.log('🔍 Searching for athlete with email:', debouncedEmail);
        
        // Match parent access request logic - query profiles table with role filter
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username, email, avatar_url, role')
          .eq('email', debouncedEmail.toLowerCase().trim())
          .eq('role', 'athlete')
          .maybeSingle();

        if (error) {
          console.error('❌ Error searching for athlete:', error);
          setSearchError('Error searching for athlete');
          setFoundAthlete(null);
          return;
        }

        if (data) {
          console.log('✅ Found athlete:', data);
          setFoundAthlete({
            id: data.id,
            username: data.username,
            email: data.email,
            avatar_url: data.avatar_url
          });
          setSearchError(null);
        } else {
          console.log('⚠️ No athlete found with email:', debouncedEmail);
          setFoundAthlete(null);
          setSearchError('No athlete found with that email address');
        }
      } catch (err) {
        console.error('❌ Search error:', err);
        setSearchError('Error searching for athlete');
        setFoundAthlete(null);
      } finally {
        setIsSearching(false);
      }
    };

    searchAthlete();
  }, [debouncedEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!foundAthlete) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid athlete email address.',
        variant: 'destructive',
      });
      return;
    }

    // Validate required data before submission
    if (!foundAthlete.id) {
      toast({
        title: 'Validation Error',
        description: 'Athlete profile ID is missing.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('🔄 Submitting request for athlete:', {
        athleteId: foundAthlete.id,
        athleteUsername: foundAthlete.username,
        message: message.trim() || undefined
      });

      await onSubmit(foundAthlete.id, message.trim() || undefined);
      
      toast({
        title: 'Request Submitted',
        description: `Your request to access ${foundAthlete.username}'s profile has been submitted.`,
      });

      // Reset form
      setAthleteEmail('');
      setMessage('');
      setFoundAthlete(null);
      setSearchError(null);
      onClose();
    } catch (error) {
      console.error('❌ Error submitting request:', error);
      toast({
        title: 'Submission Failed',
        description: 'Failed to submit your request. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setAthleteEmail('');
      setMessage('');
      setFoundAthlete(null);
      setSearchError(null);
      onClose();
    }
  };

  const canSubmit = foundAthlete && !isSubmitting && !isSearching;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Athlete Assignment</DialogTitle>
          <DialogDescription>
            Request view-only access to an athlete's profile and recruiting activities.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="athleteEmail">Athlete Email Address *</Label>
            <Input
              id="athleteEmail"
              type="email"
              placeholder="athlete@example.com"
              value={athleteEmail}
              onChange={(e) => setAthleteEmail(e.target.value)}
              disabled={isSubmitting}
              required
              autoComplete="off"
            />
            
            {/* Search Status Display */}
            <div className="mt-2 min-h-[24px]">
              {isSearching && athleteEmail.trim() && (
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span>Searching...</span>
                </div>
              )}
              
              {foundAthlete && !isSearching && (
                <div className="flex items-center space-x-2 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <div className="flex items-center space-x-2">
                    {foundAthlete.avatar_url ? (
                      <img 
                        src={foundAthlete.avatar_url} 
                        alt={foundAthlete.username}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                    <span>Athlete found: {foundAthlete.username}</span>
                  </div>
                </div>
              )}
              
              {searchError && !isSearching && athleteEmail.trim() && (
                <div className="flex items-center space-x-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>{searchError}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="message">Optional Message</Label>
            <Textarea
              id="message"
              placeholder="Hi, I'd like to help guide your recruiting process..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSubmitting}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!canSubmit}
              className={!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RequestAssignmentsModal;